import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, User, ShieldCheck } from 'lucide-react';
import permissionService from '../services/permissionService';
import userService from '../services/userService';
import { useAuth } from '../contexts/AuthContext';
import { PERMISSION_RESOURCES, resourceLabel } from '../config/permissionResources';
import '../styles/permissions-manager.css';

const isSuperAdminUser = (u) =>
  String(u?.role || '').toLowerCase() === 'admin';

const PermissionsManager = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedResources, setSelectedResources] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);

  const { isAdmin, hasAccess, user: currentUser } = useAuth();
  const isDelegated = !isAdmin;
  const isOwnedByMe = (u) => Number(u?.createdBy) === Number(currentUser?.id);

  const grantableResources = useMemo(() => {
    if (isAdmin) return PERMISSION_RESOURCES;
    return PERMISSION_RESOURCES.filter(
      (r) =>
        hasAccess(r.value) ||
        (r.value.startsWith('station:') && hasAccess('stations'))
    );
  }, [isAdmin, hasAccess]);

  const grantableGroups = useMemo(() => {
    const groups = new Map();
    grantableResources.forEach((r) => {
      if (!groups.has(r.group)) groups.set(r.group, []);
      groups.get(r.group).push(r);
    });
    return groups;
  }, [grantableResources]);

  const manageableUsers = useMemo(
    () =>
      users.filter((u) => {
        if (isSuperAdminUser(u)) return false;
        if (isDelegated) {
          return String(u.role || '').toLowerCase() === 'viewer' && isOwnedByMe(u);
        }
        return true;
      }),
    [users, isDelegated, currentUser?.id]
  );

  const groupedByUser = useMemo(() => {
    const map = new Map();
    permissions
      .filter((p) => p.subjectType === 'user')
      .forEach((p) => {
        const u = users.find((x) => String(x.id) === String(p.subjectId));
        if (!u || isSuperAdminUser(u)) return;
        if (isDelegated && !isOwnedByMe(u)) return;
        const key = String(p.subjectId);
        if (!map.has(key)) map.set(key, { user: u, grants: [] });
        map.get(key).grants.push(p);
      });
    return Array.from(map.values()).sort((a, b) =>
      `${a.user.firstName} ${a.user.lastName}`.localeCompare(
        `${b.user.firstName} ${b.user.lastName}`
      )
    );
  }, [permissions, users, isDelegated, currentUser?.id]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groupedByUser;
    return groupedByUser.filter(({ user: u }) =>
      `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(q)
    );
  }, [groupedByUser, search]);

  const showMsg = (text, type = 'success') => {
    setNotice({ text, type });
    setTimeout(() => setNotice(null), 2500);
  };

  const reload = async () => {
    setLoading(true);
    setError('');
    try {
      const resourcesToLoad = isAdmin ? PERMISSION_RESOURCES : grantableResources;
      const [permResults, usersRes] = await Promise.all([
        Promise.all(resourcesToLoad.map((r) => permissionService.list(r.value))),
        userService.list(),
      ]);
      setPermissions(
        permResults.flatMap((res) => (res.success ? res.data.permissions || [] : []))
      );
      if (usersRes.success) setUsers(usersRes.data.users || []);
    } catch {
      setError('Could not load permissions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const toggleResource = (value) => {
    setSelectedResources((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleGrant = async () => {
    if (!selectedUserId) {
      showMsg('Select a user first', 'error');
      return;
    }
    if (!selectedResources.length) {
      showMsg('Select at least one access item', 'error');
      return;
    }

    const target = users.find((u) => String(u.id) === String(selectedUserId));
    if (isDelegated && (String(target?.role || '').toLowerCase() !== 'viewer' || !isOwnedByMe(target))) {
      showMsg('You can grant access only to users you created', 'error');
      return;
    }

    let ok = true;
    for (const res of selectedResources) {
      const result = await permissionService.save({
        subjectType: 'user',
        subjectId: selectedUserId,
        permission: 'viewer',
        resource: res,
      });
      if (!result.success) {
        showMsg(result.message || 'Failed to save', 'error');
        ok = false;
        break;
      }
    }

    if (ok) {
      await reload();
      setSelectedResources([]);
      showMsg('Access saved', 'success');
    }
  };

  const handleRemove = async () => {
    if (!confirmRemove) return;
    const { grant } = confirmRemove;
    const res = await permissionService.remove({
      subjectType: 'user',
      subjectId: grant.subjectId,
      resource: grant.resource,
    });
    setConfirmRemove(null);
    if (!res.success) {
      showMsg(res.message || 'Could not remove access', 'error');
      return;
    }
    await reload();
    showMsg('Access removed', 'success');
  };

  if (!isOpen) return null;

  return (
    <div className="pm-overlay" onClick={onClose}>
      <div className="pm-panel" onClick={(e) => e.stopPropagation()}>
        <header className="pm-panel-head">
          <div className="pm-head-icon">
            <ShieldCheck size={22} />
          </div>
          <div className="pm-head-text">
            <h2>Manage Access</h2>
            <p>
              Choose a user and tick what they can use in the menu.
              Super Admins are hidden and always have full access.
              Users must log in again after changes.
            </p>
          </div>
          <button type="button" className="pm-icon-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="pm-panel-body">
          {error && <div className="pm-alert pm-alert-error">{error}</div>}

          <div className="pm-layout">
            <section className="pm-grant-card">
              <h3>
                <User size={16} /> Grant access
              </h3>

              <label className="pm-field">
                <span>User</span>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                >
                  <option value="">Select user…</option>
                  {manageableUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.firstName} {u.lastName} — {u.email}
                    </option>
                  ))}
                </select>
              </label>

              <div className="pm-field">
                <span>Menu &amp; features</span>
                <div className="pm-checkboxes">
                  {Array.from(grantableGroups.entries()).map(([group, items]) => (
                    <div key={group} className="pm-check-group">
                      <div className="pm-check-group-title">{group}</div>
                      <div className="pm-check-grid">
                        {items.map((item) => (
                          <label key={item.value} className="pm-check">
                            <input
                              type="checkbox"
                              checked={selectedResources.includes(item.value)}
                              onChange={() => toggleResource(item.value)}
                            />
                            {item.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="button"
                className="pm-btn"
                onClick={handleGrant}
                disabled={loading || !selectedUserId || !selectedResources.length}
              >
                Save access
              </button>
            </section>

            <section className="pm-list-card">
              <div className="pm-list-head">
                <h3>Who has access</h3>
                <div className="pm-search-box">
                  <Search size={15} />
                  <input
                    type="search"
                    placeholder="Search…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>

              {loading && !groupedByUser.length ? (
                <p className="pm-muted">Loading…</p>
              ) : filteredGroups.length === 0 ? (
                <p className="pm-muted">No access assigned yet.</p>
              ) : (
                <div className="pm-user-rows">
                  {filteredGroups.map(({ user: u, grants }) => (
                    <div key={u.id} className="pm-user-row">
                      <div className="pm-user-row-top">
                        <strong>{u.firstName} {u.lastName}</strong>
                        <span className="pm-email">{u.email}</span>
                      </div>
                      <div className="pm-grant-chips">
                        {grants.map((g) => (
                          <span key={`${g.resource}-${g.id || g.permission}`} className="pm-grant-chip">
                            {resourceLabel(g.resource)}
                            <button
                              type="button"
                              className="pm-chip-remove"
                              title="Remove"
                              onClick={() =>
                                setConfirmRemove({
                                  grant: g,
                                  label: `${u.firstName} ${u.lastName} — ${resourceLabel(g.resource)}`,
                                })
                              }
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>

        {notice && (
          <div className={`pm-toast pm-toast-${notice.type}`}>{notice.text}</div>
        )}

        {confirmRemove && (
          <div className="pm-confirm-overlay" onClick={() => setConfirmRemove(null)}>
            <div className="pm-confirm" onClick={(e) => e.stopPropagation()}>
              <p>
                Remove <strong>{confirmRemove.label}</strong>?
              </p>
              <div className="pm-confirm-btns">
                <button type="button" className="pm-btn-secondary" onClick={() => setConfirmRemove(null)}>
                  Cancel
                </button>
                <button type="button" className="pm-btn-danger" onClick={handleRemove}>
                  Remove
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PermissionsManager;
